import React, {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import styled from "styled-components";
import Strings from "../../Constants/Strings";
import {fetchData} from "../../Utils/Server";
import Endpoints from "../../Constants/Endpoints";
import PageLayout from "../Layout/PageLayout";
import {StatCard, Card, CardTitleBar, Tabs, DataTable, StatusBadge, Button, Spinner} from "../UI";
import {tokens} from "../../theme/tokens";

const EMPTY_DASHBOARD = {
    overview: {},
    requests: {},
    revenue: {},
    request_pipeline: {},
    users: {},
    riders: {},
    businesses: {},
    invoices: {},
    recent_requests: [],
};

// Presentation-only styled helpers (layout + section headings).
const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(${(p) => p.$min || 190}px, 1fr));
    gap: ${tokens.spacing[4]};
    margin-bottom: ${tokens.spacing[5]};
`;

const SectionTitle = styled.h2`
    font-family: ${tokens.typography.fonts.body};
    font-size: ${tokens.typography.size.lg};
    font-weight: ${tokens.typography.weight.bold};
    color: ${tokens.colors.heading};
    letter-spacing: ${tokens.typography.tracking.tight};
    margin: ${tokens.spacing[6]} 0 ${tokens.spacing[4]};
`;

const Dashboard = props => {

    const [isLoading, setIsLoading] = useState(false);
    const [dashboard, setDashboard] = useState(EMPTY_DASHBOARD);
    const [tab, setTab] = useState("requests");

    useEffect(() => {
        document.title = "Dashboard | " + Strings.APP_DESCRIPTION;
        loadDashboard();
    }, []);

    const loadDashboard = () => {
        setIsLoading(true);
        fetchData(Endpoints.ADMIN_DASHBOARD)
            .then(res => res.json())
            .then(res => {
                setIsLoading(false);
                if (res.success === 1 && res.data) {
                    setDashboard({...EMPTY_DASHBOARD, ...res.data});
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.log(error);
            });
    };

    const overview = dashboard.overview || {};
    const requests = dashboard.requests || {};
    const revenue = dashboard.revenue || {};
    const pipeline = dashboard.request_pipeline || {};
    const users = dashboard.users || {};
    const riders = dashboard.riders || {};
    const businesses = dashboard.businesses || {};
    const invoices = dashboard.invoices || {};
    const recent = dashboard.recent_requests || [];

    const overviewStats = [
        {label: "Total Revenue", value: overview.formatted_total_revenue || 0, accent: true},
        {label: "Total Requests", value: overview.total_requests || 0},
        {label: "Total Users", value: overview.total_users || 0},
        {label: "Total Riders", value: overview.total_riders || 0},
        {label: "Total Businesses", value: overview.total_businesses || 0},
    ];

    const pipelineStats = [
        {label: "Incoming", value: pipeline.incoming},
        {label: "Assigned", value: pipeline.assigned},
        {label: "Ongoing", value: pipeline.ongoing},
        {label: "Completed", value: pipeline.completed},
        {label: "Cancelled", value: pipeline.cancelled},
        {label: "Late", value: pipeline.late},
        {label: "Bounced", value: pipeline.bounced},
        {label: "Call Centre", value: pipeline.cc},
        {label: "Rider 1", value: pipeline.rone},
        {label: "Rider 2", value: pipeline.rtwo},
        {label: "Rider 3", value: pipeline.rthree},
        {label: "Rider 4", value: pipeline.rfour},
    ];

    const requestTiles = [
        {label: "Today", total: requests.today, onTime: requests.on_time_today},
        {label: "Yesterday", total: requests.yesterday, onTime: null},
        {label: "This Week", total: requests.this_week, onTime: requests.on_time_this_week},
        {label: "Last Week", total: requests.last_week, onTime: requests.on_time_last_week},
        {label: "This Month", total: requests.this_month, onTime: requests.on_time_this_month},
        {label: "Last Month", total: requests.last_month, onTime: requests.on_time_last_month},
    ];

    const revenueTiles = [
        {label: "Today", value: revenue.formatted_today},
        {label: "Yesterday", value: revenue.formatted_yesterday},
        {label: "This Week", value: revenue.formatted_this_week},
        {label: "Last Week", value: revenue.formatted_last_week},
        {label: "This Month", value: revenue.formatted_this_month},
        {label: "Last Month", value: revenue.formatted_last_month},
    ];

    const userSignups = [
        {label: "Today", value: users.new_today},
        {label: "Yesterday", value: users.new_yesterday},
        {label: "This Week", value: users.new_this_week},
        {label: "Last Week", value: users.new_last_week},
        {label: "This Month", value: users.new_this_month},
        {label: "Last Month", value: users.new_last_month},
    ];

    const riderSignups = [
        {label: "Today", value: riders.new_today},
        {label: "Yesterday", value: riders.new_yesterday},
        {label: "This Week", value: riders.new_this_week},
        {label: "Last Week", value: riders.new_last_week},
        {label: "This Month", value: riders.new_this_month},
        {label: "Last Month", value: riders.new_last_month},
    ];

    const businessSignups = [
        {label: "Today", value: businesses.new_today},
        {label: "Yesterday", value: businesses.new_yesterday},
        {label: "This Week", value: businesses.new_this_week},
        {label: "Last Week", value: businesses.new_last_week},
        {label: "This Month", value: businesses.new_this_month},
        {label: "Last Month", value: businesses.new_last_month},
    ];

    const recentColumns = [
        {
            name: "Code",
            grow: 1,
            cell: (row) => <b style={{color: tokens.colors.heading}}>#{row.request_code}</b>,
        },
        {name: "Pick Up", grow: 2, selector: (row) => row.pick_up_location, wrap: true},
        {name: "Type", grow: 1, selector: (row) => row.delivery_type},
        {name: "Status", grow: 1, cell: (row) => <StatusBadge status={row.status}>{row.status}</StatusBadge>},
        {name: "Fee", grow: 1, selector: (row) => row.formatted_price},
        {
            name: "Pick Up Time",
            grow: 2,
            selector: (row) => row.pick_up_date_time_string || row.date_string,
        },
        {
            name: "",
            grow: 1,
            right: true,
            cell: (row) => (
                <Link to={"/request/" + row.id}>
                    <Button size="sm" variant="outline">View</Button>
                </Link>
            ),
        },
    ];

    const tabs = [
        {key: "requests", label: "Requests"},
        {key: "revenue", label: "Revenue"},
        {key: "users", label: "Users"},
        {key: "riders", label: "Riders"},
        {key: "businesses", label: "Businesses"},
        {key: "invoices", label: "Invoices"},
    ];

    return (
        <PageLayout title="Dashboard">

            {isLoading && <Spinner center label="Loading dashboard…"/>}

            {!isLoading &&
            <>
                <Grid $min={200}>
                    {overviewStats.map((s, k) => (
                        <StatCard key={k} label={s.label} value={s.value} accent={!!s.accent}/>
                    ))}
                </Grid>

                <SectionTitle>Request Pipeline</SectionTitle>
                <Grid $min={150}>
                    {pipelineStats.map((s, k) => (
                        <StatCard key={k} label={s.label} value={s.value || 0} accent={false}/>
                    ))}
                </Grid>

                <Card padded={false} style={{marginTop: tokens.spacing[6]}}>
                    <div style={{padding: `0 ${tokens.spacing[5]}`}}>
                        <Tabs tabs={tabs} value={tab} onChange={setTab}/>
                    </div>
                    <div style={{padding: `0 ${tokens.spacing[5]} ${tokens.spacing[5]}`}}>

                        {tab === "requests" &&
                        <Grid $min={200}>
                            {requestTiles.map((item, k) => (
                                <StatCard
                                    key={k}
                                    label={item.label}
                                    value={item.total || 0}
                                    accent={false}
                                    hint={item.onTime !== null ? `On time: ${item.onTime || 0}` : undefined}
                                />
                            ))}
                        </Grid>
                        }

                        {tab === "revenue" &&
                        <Grid $min={200}>
                            {revenueTiles.map((item, k) => (
                                <StatCard key={k} label={item.label} value={item.value || 0} accent={false}/>
                            ))}
                        </Grid>
                        }

                        {tab === "users" &&
                        <>
                            <Grid $min={180}>
                                <StatCard label="Total" value={users.total || 0} accent={false}/>
                                <StatCard label="Active" value={users.active || 0} accent={false}/>
                                <StatCard label="Inactive" value={users.inactive || 0} accent={false}/>
                                <StatCard label="Blocked" value={users.blocked || 0} accent={false}/>
                            </Grid>
                            <SectionTitle>New Sign-ups</SectionTitle>
                            <Grid $min={180}>
                                {userSignups.map((item, k) => (
                                    <StatCard key={k} label={item.label} value={item.value || 0} accent={false}/>
                                ))}
                            </Grid>
                        </>
                        }

                        {tab === "riders" &&
                        <>
                            <Grid $min={150}>
                                <StatCard label="Total" value={riders.total || 0} accent={false}/>
                                <StatCard label="Active" value={riders.active || 0} accent={false}/>
                                <StatCard label="Available" value={riders.available || 0} accent={false}/>
                                <StatCard label="Occupied" value={riders.occupied || 0} accent={false}/>
                                <StatCard label="Inactive" value={riders.inactive || 0} accent={false}/>
                                <StatCard label="Suspended" value={riders.suspended || 0} accent={false}/>
                            </Grid>
                            <SectionTitle>New Sign-ups</SectionTitle>
                            <Grid $min={180}>
                                {riderSignups.map((item, k) => (
                                    <StatCard key={k} label={item.label} value={item.value || 0} accent={false}/>
                                ))}
                            </Grid>
                        </>
                        }

                        {tab === "businesses" &&
                        <>
                            <Grid $min={200}>
                                <StatCard label="Total" value={businesses.total || 0} accent={false}/>
                                <StatCard label="Active" value={businesses.active || 0} accent={false}/>
                                <StatCard label="Inactive" value={businesses.inactive || 0} accent={false}/>
                            </Grid>
                            <SectionTitle>New Sign-ups</SectionTitle>
                            <Grid $min={180}>
                                {businessSignups.map((item, k) => (
                                    <StatCard key={k} label={item.label} value={item.value || 0} accent={false}/>
                                ))}
                            </Grid>
                        </>
                        }

                        {tab === "invoices" &&
                        <Grid $min={190}>
                            <StatCard label="Total Amount" value={invoices.formatted_total_amount || 0} accent={false}/>
                            <StatCard label="Total Paid" value={invoices.formatted_total_paid || 0} accent={false}/>
                            <StatCard label="Outstanding Balance" value={invoices.formatted_total_balance || 0} accent={false}/>
                            <StatCard label="Total Invoices" value={invoices.total || 0} accent={false}/>
                            <StatCard label="Paid" value={invoices.paid || 0} accent={false}/>
                            <StatCard label="Unpaid" value={invoices.unpaid || 0} accent={false}/>
                            <StatCard label="Pending" value={invoices.pending || 0} accent={false}/>
                        </Grid>
                        }

                    </div>
                </Card>

                <Card padded={false} style={{marginTop: tokens.spacing[6]}}>
                    <CardTitleBar title="Recent Requests"/>
                    <DataTable
                        columns={recentColumns}
                        data={recent}
                        noDataComponent={
                            <div style={{padding: 24, color: tokens.colors.muted}}>No recent requests.</div>
                        }
                    />
                </Card>
            </>
            }
        </PageLayout>
    );
};

export default Dashboard;
